import { OtsDateTime } from '../../components/OtsDateTime';
import { useState } from 'react';
import type { RrcCompanyProfile } from '@ots-frontend/ots-data';
import {
  OtsDataTable,
  OtsDataTableHead,
  OtsDataTableBody,
  OtsDataTableRow,
  OtsDataTableHeaderCell,
  OtsDataTableCell,
  OtsDataTableSortableHeaderCell,
} from '../../components/OtsDataTable';
import {
  OtsDataTableLink,
  OtsDataTableEmpty,
} from '../../components/OtsDataTableCells';
import { OtsTablePanel } from '../../components/OtsTablePanel';
import { OtsSearchInput } from '../../components/OtsSearchInput';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsSwitch } from '../../components/OtsSwitch';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { companyProfileCodeLabel } from './rrc-company-profile-ui';

export type RrcCompanyProfilesViewProps = {
  profiles: RrcCompanyProfile[];
  loading?: boolean;
  error?: string;
  compact?: boolean;
  fill?: boolean;
  includeHistory?: boolean;
  onIncludeHistory?: (value: boolean) => void;
  onOpen: (profile: RrcCompanyProfile) => void;
  onNew?: () => void;
  onRefresh?: () => void;
};
export const RrcCompanyProfilesView = ({
  profiles,
  loading,
  error,
  compact,
  fill = false,
  includeHistory,
  onIncludeHistory,
  onOpen,
  onNew,
  onRefresh,
}: RrcCompanyProfilesViewProps) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [ascending, setAscending] = useState(true);
  const filtered = profiles
    .filter((profile) =>
      [
        profile.orgName,
        profile.companyName,
        profile.p5Number,
        profile.permitNumber,
        companyProfileCodeLabel(profile.role),
        companyProfileCodeLabel(profile.rrcDistrict),
        profile.status,
      ].some((value) =>
        value?.toLowerCase().includes(search.toLowerCase().trim())
      )
    )
    .sort(
      (a, b) =>
        (ascending ? 1 : -1) *
          (a.orgName ?? '').localeCompare(b.orgName ?? '') ||
        b.version - a.version
    );
  const pageSize = 15;
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(filtered.length / pageSize))
  );
  return (
    <OtsTablePanel
      title="RRC Company Profiles"
      compact={compact}
      fill={fill}
      description={`${filtered.length} profiles`}
      actions={
        <>
          {onRefresh && (
            <OtsActionButton
              size="compact"
              onClick={onRefresh}
              disabled={loading}
            >
              Refresh
            </OtsActionButton>
          )}
          {onNew && (
            <OtsActionButton variant="primary" size="compact" onClick={onNew}>
              New company profile
            </OtsActionButton>
          )}
        </>
      }
      toolbar={
        <>
          <OtsSearchInput
            label="Search company profiles"
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setPage(1);
            }}
            size={compact ? 'compact' : 'default'}
          />
          {onIncludeHistory && (
            <OtsSwitch
              label="Include previous versions"
              checked={includeHistory ?? false}
              onCheckedChange={(value) => {
                setPage(1);
                onIncludeHistory(value);
              }}
              size={compact ? 'compact' : 'default'}
            />
          )}
        </>
      }
      pagination={{
        page: currentPage,
        pageSize,
        totalItems: filtered.length,
        onPageChange: setPage,
        itemLabel: 'profiles',
        disabled: loading,
      }}
    >
      {loading ? (
        <OtsDataTableEmpty loading loadingText="Loading company profiles…">
          {null}
        </OtsDataTableEmpty>
      ) : error ? (
        <div className="p-4">
          <OtsInlineAlert
            tone="danger"
            title="Company profiles unavailable"
            action={
              onRefresh && (
                <OtsActionButton size="compact" onClick={onRefresh}>
                  Try again
                </OtsActionButton>
              )
            }
          >
            {error}
          </OtsInlineAlert>
        </div>
      ) : !filtered.length ? (
        <OtsDataTableEmpty compact={compact}>
          {search
            ? 'No company profiles match your search.'
            : 'No company profiles have been configured.'}
        </OtsDataTableEmpty>
      ) : (
        <OtsDataTable
          aria-label="RRC company profiles"
          density={compact ? 'compact' : 'default'}
          contained={false}
        >
          <OtsDataTableHead>
            <OtsDataTableRow>
              <OtsDataTableSortableHeaderCell
                direction={ascending ? 'asc' : 'desc'}
                onSort={() => setAscending(!ascending)}
              >
                Organization
              </OtsDataTableSortableHeaderCell>
              {[
                'Role',
                'District',
                'Effective start',
                'Effective end',
                'Status',
                'Version',
              ].map((label) => (
                <OtsDataTableHeaderCell key={label}>
                  {label}
                </OtsDataTableHeaderCell>
              ))}
            </OtsDataTableRow>
          </OtsDataTableHead>
          <OtsDataTableBody>
            {filtered
              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
              .map((profile) => (
                <OtsDataTableRow key={profile.id}>
                  <OtsDataTableCell>
                    <OtsDataTableLink
                      onClick={() => onOpen(profile)}
                      ariaLabel={`Open ${
                        profile.orgName ??
                        profile.companyName ??
                        'company profile'
                      }, version ${profile.version}`}
                    >
                      {profile.orgName ??
                        profile.companyName ??
                        'Unnamed organization'}
                    </OtsDataTableLink>
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    {companyProfileCodeLabel(profile.role)}
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    {companyProfileCodeLabel(profile.rrcDistrict)}
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <OtsDateTime
                      value={profile.effectiveStartDttm}
                      empty="Not set"
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <OtsDateTime
                      value={profile.effectiveEndDttm}
                      empty="No end date"
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <ComplianceStatusBadge
                      status={profile.status}
                      compact={compact}
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>{profile.version}</OtsDataTableCell>
                </OtsDataTableRow>
              ))}
          </OtsDataTableBody>
        </OtsDataTable>
      )}
    </OtsTablePanel>
  );
};
